import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';

import { getBMI } from '@utils/numberUtils';

import { MainContext } from '@components/context/MainProvider';
import { trackQuizBegin } from '@nextUtils/trackingUtils';
import { vitalityPromoCodeRegex } from '@nextUtils/generalUtils';
import useDeepCompareEffect from './useDeepComparison';
import { useQuiz } from '@components/context/QuizProvider';
import { calculateGoalWeight, getPercentageGoal } from '@nextUtils/nhsUtils';
import { ErrorTracking } from '../common/errorTracking';
import { clearLocalStorage } from '../components/NhsSignup/helpers';
import { getAPIUrl } from '@nextUtils/urlUtils';
import { useAbTestStore } from '../state/abTestStore';

export const useCheckPromoCodeForGetPlan = (): void => {
  const {
    query: { promo },
    push,
  } = useRouter();

  const checkPromoCode = useCallback(() => {
    if (!promo) {
      return;
    }

    // Redirect Vitality users with old sign-up links to the new vitality sign-up flow
    if (typeof promo === 'string' && vitalityPromoCodeRegex.test(promo)) {
      push(`/partnerships/sn-vitality`);
      return;
    }

    fetch(`${getAPIUrl()}/api/check-promo-code/${promo}`)
      .then(response => {
        if (!response.ok) {
          throw new Error();
        }

        return response.json();
      })
      .then(json => {
        const { isValidCode } = json;

        if (isValidCode) {
          Cookies.set('promo-code', promo, {
            expires: 365,
            path: '/',
          });
        }
      })
      .catch(err => {
        // If we do not find a valid promo-code via the API, we remove the promo-code in the query
        ErrorTracking.track(err, {
          message: 'Error checking validity of promo-code',
        });
      });
  }, [promo, push]);

  useEffect(() => {
    checkPromoCode();
  }, [checkPromoCode]);
};

export const useStartQuiz = (): void => {
  useCheckPromoCodeForGetPlan();
  const { dispatch, state } = useQuiz();
  const { user } = useContext(MainContext);
  const abTests = useAbTestStore(abTestsState => abTestsState.abTests);
  const {
    query: { promo: promoQueryParam, resetAnswers },
    isReady,
  } = useRouter();

  useEffect(() => {
    // If a user has visited the old quiz flow, we need to clear their answers as the quiz structure has changed
    if (resetAnswers === 'true') {
      clearLocalStorage('cached-quiz');
    }

    // We use this action to determine whether to redirect a user to the start of the quiz
    // if they visit a page that is not the start of the quiz and they have not started the quiz
    // the user will be redirected to the start of the quiz.
    if (!state.hasStartedQuiz) {
      dispatch({ type: 'HAS_STARTED_QUIZ', payload: true });
    }
  }, [dispatch, state.hasStartedQuiz, resetAnswers]);

  // Use useDeepCompareEffect for deep comparison of abTests
  useDeepCompareEffect(() => {
    if (abTests.type === 'Success' && isReady && !state.hasStartedQuiz) {
      const promo =
        typeof promoQueryParam === 'string' ? promoQueryParam : undefined;
      trackQuizBegin(promo, user?._id);
    }
  }, [abTests, isReady, promoQueryParam, user?._id, state.hasStartedQuiz]);
};

export const useMultiChoiceContinueButton = (stateValue: string[]): boolean => {
  const [showContinueButton, setShowContinueButton] = useState(false);

  useEffect(() => {
    setShowContinueButton(stateValue.length > 0);
  }, [stateValue]);

  return showContinueButton;
};

// Calculate the percentage goal + goal weight for the user
// and save it to state when goalWeightKg changes
export const usePercentageGoal = (): void => {
  const {
    state: { goalWeightKg, heightCm },
    dispatch,
  } = useQuiz();

  const setPercentageGoal = useCallback(
    (percentage: number): void => {
      dispatch({ type: 'SET_PERCENTAGE_GOAL', payload: percentage });
    },
    [dispatch],
  );

  useEffect(() => {
    if (!goalWeightKg || !heightCm) {
      return;
    }

    const newGoalBmi = getBMI(goalWeightKg, heightCm);

    if (!newGoalBmi) {
      return;
    }

    const newPercentageGoal = getPercentageGoal(newGoalBmi);
    setPercentageGoal(newPercentageGoal);
  }, [goalWeightKg, heightCm, setPercentageGoal]);
};

export type GoalWeightType = {
  kg: number;
  lbs: number;
  st: number;
};

// Calculate the goal weight for the user when height or weight change
export const useGoalWeight = (): void => {
  const {
    dispatch,
    state: { heightCm, weightKg },
  } = useQuiz();

  const updateGoalWeight = useCallback(
    (weight: GoalWeightType): void => {
      dispatch({
        type: 'SET_GOAL_WEIGHTS',
        payload: { ...weight },
      });
    },
    [dispatch],
  );

  useEffect(() => {
    if (!heightCm || !weightKg) {
      return;
    }

    const goalWeight = calculateGoalWeight({ heightCm, weightKg });
    const { goalKg, goalLbs, goalSt } = goalWeight || {};

    if (!goalKg || !goalLbs || !goalSt) {
      return;
    }

    updateGoalWeight({ kg: goalKg, lbs: goalLbs, st: goalSt });
  }, [heightCm, updateGoalWeight, weightKg]);
};
